import { authService } from '@/api/services/auth';
import { authHelper } from '@/lib/localStore';
import { AUTH_SESSION_REDIRECT_PENDING_FLAG, LOGOUT_IN_PROGRESS_FLAG } from '@/config/constants';

/** Refresh access token this many seconds before JWT `exp`. */
export const ACCESS_TOKEN_REFRESH_BUFFER_SECONDS = 120;

/** Proactive refresh only while the user was active within this window. */
export const ACTIVE_USER_WINDOW_MS = 10 * 60 * 1000;

/** After the tab was hidden at least this long, refresh on resume (opaque tokens). */
export const TAB_HIDDEN_REFRESH_THRESHOLD_MS = 60 * 1000;

export type SessionRefreshReason = 'proactive' | 'tab-resume' | 'request';

let refreshInFlight: Promise<boolean> | null = null;
let tabHiddenAt: number | null = null;

/**
 * Parse JWT `exp` claim as unix timestamp in seconds.
 */
export function getAccessTokenExpirySeconds(token: string | null): number | null {
  if (!token) {
    return null;
  }

  try {
    const payload = token.split('.')[1];
    if (!payload) {
      return null;
    }

    const base64 = payload.replace(/-/g, '+').replace(/_/g, '/');
    const parsed = JSON.parse(atob(base64)) as { exp?: number };
    return typeof parsed.exp === 'number' ? parsed.exp : null;
  } catch {
    return null;
  }
}

/**
 * Seconds until access token expiry (negative when already expired).
 */
export function getAccessTokenTtlSeconds(token: string | null = authHelper.token): number | null {
  const exp = getAccessTokenExpirySeconds(token);
  if (exp === null) {
    return null;
  }

  return exp - Math.floor(Date.now() / 1000);
}

/**
 * Whether the access token is expired or within the proactive refresh buffer.
 */
export function isAccessTokenStale(token: string | null = authHelper.token): boolean {
  const ttl = getAccessTokenTtlSeconds(token);
  if (ttl === null) {
    return false;
  }

  return ttl <= ACCESS_TOKEN_REFRESH_BUFFER_SECONDS;
}

/**
 * Record when the browser tab became hidden (used for opaque-token refresh on resume).
 */
export function markTabVisibilityState(state: DocumentVisibilityState): void {
  if (state === 'hidden') {
    tabHiddenAt = Date.now();
    return;
  }

  if (state === 'visible') {
    // Keep tabHiddenAt until ensureSessionFresh reads it on resume.
  }
}

/**
 * Milliseconds the tab was hidden before the current visible transition.
 */
export function consumeTabHiddenDurationMs(): number {
  if (tabHiddenAt === null) {
    return 0;
  }

  const duration = Date.now() - tabHiddenAt;
  tabHiddenAt = null;
  return duration;
}

function shouldRefreshForProactiveUse(): boolean {
  if (!authHelper.token || !authHelper.refreshToken) {
    return false;
  }

  const ttl = getAccessTokenTtlSeconds();
  if (ttl === null) {
    return false;
  }

  return ttl <= ACCESS_TOKEN_REFRESH_BUFFER_SECONDS;
}

function shouldRefreshOnTabResume(hiddenDurationMs: number): boolean {
  if (!authHelper.token || !authHelper.refreshToken) {
    return false;
  }

  const ttl = getAccessTokenTtlSeconds();
  if (ttl !== null) {
    return ttl <= ACCESS_TOKEN_REFRESH_BUFFER_SECONDS;
  }

  return hiddenDurationMs >= TAB_HIDDEN_REFRESH_THRESHOLD_MS;
}

/**
 * Refresh auth token once and share the same in-flight promise across callers.
 */
export async function refreshSessionToken(): Promise<boolean> {
  if (refreshInFlight) {
    return refreshInFlight;
  }

  const refreshToken = authHelper.refreshToken;
  if (!refreshToken) {
    return false;
  }

  refreshInFlight = (async () => {
    await authService.refreshToken({ refreshToken });
    window.sessionStorage.removeItem(AUTH_SESSION_REDIRECT_PENDING_FLAG);
    return true;
  })();

  try {
    return await refreshInFlight;
  } catch {
    return false;
  } finally {
    refreshInFlight = null;
  }
}

/**
 * Silently refresh the session when the access token is stale or the tab resumes after idle time.
 */
export async function ensureSessionFresh(options: {
  reason: SessionRefreshReason;
  isActiveUser?: boolean;
  tabHiddenDurationMs?: number;
}): Promise<boolean> {
  if (window.sessionStorage.getItem(LOGOUT_IN_PROGRESS_FLAG) === 'true') {
    return false;
  }

  if (!authHelper.token || !authHelper.refreshToken) {
    return false;
  }

  let shouldRefresh = false;

  switch (options.reason) {
    case 'tab-resume': {
      const hiddenMs = options.tabHiddenDurationMs ?? 0;
      shouldRefresh = shouldRefreshOnTabResume(hiddenMs);
      break;
    }
    case 'request':
      shouldRefresh = isAccessTokenStale();
      break;
    case 'proactive':
    default:
      if (options.isActiveUser === false) {
        return false;
      }
      shouldRefresh = shouldRefreshForProactiveUse();
      break;
  }

  if (!shouldRefresh) {
    return true;
  }

  return refreshSessionToken();
}

/**
 * Returns a promise that resolves when an in-flight session refresh completes (if any).
 */
export function waitForSessionRefreshInFlight(): Promise<void> {
  if (!refreshInFlight) {
    return Promise.resolve();
  }

  return refreshInFlight.then(() => undefined);
}
